import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, FileText, Megaphone, PenLine, Search, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";

export const Route = createFileRoute("/_authenticated/my-signatures")({
  component: MySignaturesPage,
});

interface Signed {
  toolbox: { id: string; subject: string; date: string; projectName: string | null; signedAt: string }[];
  documents: { id: string; title: string; signedAt: string; approvedAt: string | null }[];
  flha: { id: string; projectName: string | null; date: string; signedAt: string | null }[];
}

type Kind = "toolbox" | "documents" | "flha";
interface Item {
  key: string;
  kind: Kind;
  title: string;
  sub: string;
  at: string;
  to?: { name: "toolbox" | "flha"; id: string };
}

const KIND = {
  toolbox: { label: "Toolbox", icon: <Megaphone className="h-3.5 w-3.5" /> },
  documents: { label: "Document", icon: <FileText className="h-3.5 w-3.5" /> },
  flha: { label: "FLHA", icon: <ShieldCheck className="h-3.5 w-3.5" /> },
} as const;

const TABS: { key: "all" | Kind; label: string }[] = [
  { key: "all", label: "All" },
  { key: "toolbox", label: "Toolbox meetings" },
  { key: "documents", label: "Safety documents" },
  { key: "flha", label: "FLHAs" },
];

const PAGE = 25;

/** What waits for my signature, and everything I have signed: tabs by kind, search, dates, order, grouped by month. */
function MySignaturesPage() {
  const { data: waiting } = useQuery({
    queryKey: ["toolbox", "awaiting"],
    queryFn: () => apiGet<{ data: { id: string; subject: string; date: string; projectName: string | null }[] }>("/api/toolbox/awaiting"),
    staleTime: 30_000,
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-signatures"],
    queryFn: () => apiGet<{ data: Signed }>("/api/my-signatures"),
    staleTime: 30_000,
  });
  const toSign = waiting?.data ?? [];
  const signed = data?.data;

  const [tab, setTab] = useState<"all" | Kind>("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [oldestFirst, setOldestFirst] = useState(false);
  const [shown, setShown] = useState(PAGE);

  const items = useMemo<Item[]>(() => {
    if (!signed) return [];
    return [
      ...signed.toolbox.map((t): Item => ({ key: `t-${t.id}`, kind: "toolbox", title: t.subject, sub: `${formatDateOnly(t.date)}${t.projectName ? ` · ${t.projectName}` : ""}`, at: t.signedAt, to: { name: "toolbox", id: t.id } })),
      ...signed.documents.map((d): Item => ({ key: `d-${d.id}`, kind: "documents", title: d.title, sub: d.approvedAt ? "Approved" : "Signed", at: d.signedAt })),
      ...signed.flha.map((f): Item => ({ key: `f-${f.id}`, kind: "flha", title: f.projectName ?? "FLHA", sub: formatDateOnly(f.date), at: f.signedAt ?? f.date, to: { name: "flha", id: f.id } })),
    ];
  }, [signed]);

  const counts = {
    all: items.length,
    toolbox: items.filter((i) => i.kind === "toolbox").length,
    documents: items.filter((i) => i.kind === "documents").length,
    flha: items.filter((i) => i.kind === "flha").length,
  };

  const filtered = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items
      .filter((i) => tab === "all" || i.kind === tab)
      .filter((i) => words.every((w) => `${i.title} ${i.sub}`.toLowerCase().includes(w)))
      .filter((i) => (!from || i.at.slice(0, 10) >= from) && (!to || i.at.slice(0, 10) <= to))
      .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0) * (oldestFirst ? -1 : 1));
  }, [items, tab, q, from, to, oldestFirst]);

  // Group by month so a long list is easy to scan
  const groups: { month: string; rows: Item[] }[] = [];
  for (const it of filtered.slice(0, shown)) {
    const d = new Date(it.at);
    const month = Number.isNaN(d.getTime()) ? "Earlier" : d.toLocaleDateString("en-CA", { month: "long", year: "numeric" });
    const last = groups[groups.length - 1];
    if (last && last.month === month) last.rows.push(it);
    else groups.push({ month, rows: [it] });
  }

  return (
    <div className="mx-auto max-w-lg space-y-5 pb-10">
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-text-primary">My signatures</h1>
        <p className="text-sm text-muted-foreground">What is waiting for you to sign, and everything you have signed.</p>
      </div>

      {toSign.length > 0 ? (
        <div className="space-y-2 rounded-xl border-2 !border-red-600 !bg-red-100 p-3 dark:!bg-red-950/50">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
            <PenLine className="h-4 w-4" /> Waiting for your signature ({toSign.length})
          </p>
          {toSign.map((t) => (
            <Link key={t.id} to="/toolbox/$meetingId" params={{ meetingId: t.id }} className="flex items-center justify-between gap-3 rounded-lg bg-background/80 px-3 py-2 text-sm hover:bg-background">
              <span className="min-w-0">
                <span className="block truncate font-medium">{t.subject}</span>
                <span className="block truncate text-xs text-muted-foreground">Toolbox meeting · {formatDateOnly(t.date)}{t.projectName ? ` · ${t.projectName}` : ""}</span>
              </span>
              <span className="shrink-0 rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white">Sign</span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="flex items-center gap-2 rounded-xl border-2 border-emerald-600 bg-emerald-50 px-3 py-3 text-sm font-medium text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
          <Check className="h-4 w-4" /> Nothing is waiting for your signature.
        </p>
      )}

      {isLoading && <Skeleton className="h-40 w-full rounded-xl" />}
      {isError && <p className="text-sm text-destructive">Could not load your signed documents.</p>}

      {signed && (
        <div className="space-y-3">
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-hide">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setShown(PAGE);
                }}
                className={cn(
                  "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  tab === t.key ? "bg-paris-red text-white" : "border border-border bg-card text-text-secondary hover:bg-muted/50"
                )}
              >
                {t.label} ({counts[t.key]})
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setShown(PAGE);
              }}
              placeholder="Search by name, site or date..."
              className="h-11 pl-9 text-base md:text-sm"
              aria-label="Search what I signed"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input type="date" aria-label="From date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" />
            <input type="date" aria-label="To date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" />
            <button type="button" className="h-8 rounded-md border border-input bg-background px-2 text-xs" onClick={() => setOldestFirst((v) => !v)}>
              {oldestFirst ? "Oldest first" : "Newest first"}
            </button>
            {(q || from || to) && (
              <button
                type="button"
                className="text-xs text-paris-red-text underline underline-offset-2"
                onClick={() => {
                  setQ("");
                  setFrom("");
                  setTo("");
                }}
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{filtered.length} signed</p>

          {filtered.length === 0 && (
            <p className="rounded-lg border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
              {items.length === 0 ? "You have not signed anything yet." : "Nothing matches. Try another tab, search or dates."}
            </p>
          )}

          {groups.map((g) => (
            <section key={g.month} className="space-y-1.5">
              <h2 className="pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.month}</h2>
              <ul className="space-y-1.5">
                {g.rows.map((it) => {
                  const inner = (
                    <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2.5">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{it.title}</span>
                        <span className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                          {KIND[it.kind].icon} {KIND[it.kind].label} · {it.sub} · signed {formatDateOnly(it.at)}
                        </span>
                      </span>
                      <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Signed" />
                    </div>
                  );
                  return (
                    <li key={it.key}>
                      {it.to?.name === "toolbox" ? (
                        <Link to="/toolbox/$meetingId" params={{ meetingId: it.to.id }} className="block">{inner}</Link>
                      ) : it.to?.name === "flha" ? (
                        <Link to="/flha/$flhaId" params={{ flhaId: it.to.id }} className="block">{inner}</Link>
                      ) : (
                        inner
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {filtered.length > shown && (
            <button type="button" className="w-full rounded-lg border py-2 text-sm font-medium hover:bg-muted/50" onClick={() => setShown((n) => n + PAGE)}>
              Show more ({filtered.length - shown} left)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
